import { TriangleAlertIcon } from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { TabConfig } from '@/modules/common';

interface ResponsiveTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  tabs: TabConfig[];
  children: React.ReactNode;
  width?: string;
  renderMobileTabs?: (props: {
    activeTab: string;
    onTabChange: (tab: string) => void;
    tabs: TabConfig[];
  }) => React.ReactNode;
}

/**
 * A responsive tabs component that adapts between vertical desktop tabs and horizontal mobile tabs.
 * Provides flexible content rendering with support for icons and custom mobile implementations.
 *
 * @example
 * ```tsx
 * // Basic usage with default mobile tabs
 * const tabs = [
 *   { id: 'info', label: 'Project Info', icon: InfoIcon },
 *   { id: 'team', label: 'Team', icon: UsersIcon },
 *   { id: 'settings', label: 'Settings', icon: SettingsIcon }
 * ];
 *
 * <ResponsiveTabs
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   tabs={tabs}
 * >
 *   {activeTab === 'info' && <ProjectInfo />}
 *   {activeTab === 'team' && <TeamManagement />}
 *   {activeTab === 'settings' && <ProjectSettings />}
 * </ResponsiveTabs>
 *
 * // With custom mobile tabs renderer
 * <ResponsiveTabs
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   tabs={tabs}
 *   width="w-48"
 *   renderMobileTabs={({ activeTab, onTabChange, tabs }) => (
 *     <Select value={activeTab} onValueChange={onTabChange}>
 *       <SelectTrigger>
 *         <SelectValue />
 *       </SelectTrigger>
 *       <SelectContent>
 *         {tabs.map(tab => (
 *           <SelectItem key={tab.id} value={tab.id}>
 *             {tab.label}
 *           </SelectItem>
 *         ))}
 *       </SelectContent>
 *     </Select>
 *   )}
 * >
 *
 * </ResponsiveTabs>
 *
 * // For complex layouts with conditional content
 * <ResponsiveTabs
 *   activeTab={currentView}
 *   onTabChange={handleViewChange}
 *   tabs={navigationTabs}
 *   width="w-52"
 * >
 *   <Suspense fallback={<LoadingSpinner />}>
 *     {currentView === 'dashboard' && <DashboardView />}
 *     {currentView === 'analytics' && <AnalyticsView />}
 *     {currentView === 'reports' && <ReportsView />}
 *   </Suspense>
 * </ResponsiveTabs>
 * ```
 */
export function ResponsiveTabs({
  activeTab,
  onTabChange,
  tabs,
  children,
  width = 'w-[181px]',
  renderMobileTabs,
}: ResponsiveTabsProps) {
  const activeTabConfig = tabs.find(tab => tab.id === activeTab);
  const activeTabOwnsScroll = activeTabConfig?.fillAvailableHeight ?? false;

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="flex h-full w-full flex-col">
      {renderMobileTabs && (
        <div className="mb-4 md:hidden">{renderMobileTabs({ activeTab, onTabChange, tabs })}</div>
      )}

      <div className="flex min-h-0 flex-1 gap-4">
        <div className={`hidden h-fit pt-4 md:flex ${width} shrink-0`}>
          <TabsList className="h-auto w-full flex-col gap-1 bg-transparent p-0">
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                disabled={tab.disabled}
                className={cn(
                  'w-full justify-between px-4 py-2 h-auto rounded-md bg-transparent',
                  'data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none',
                  'hover:bg-accent text-foreground font-medium text-sm',
                  tab.disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
                )}
              >
                <div className="flex items-center gap-2">
                  {tab.icon && <tab.icon className="h-4 w-4" />}
                  {tab.label}
                </div>
                <div className="flex gap-1">
                  {tab.hasWarning && <TriangleAlertIcon size={16} className="text-amber-600" />}
                  {tab.hasError && <TriangleAlertIcon size={16} className="text-destructive" />}
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div
          className={cn(
            'min-w-0 flex-1 py-4',
            // When the tab owns its own scroll, let it reach the right edge so
            // its scrollbar sits flush to the right (no `pr-4` inset). Other
            // tabs keep the right padding.
            activeTabOwnsScroll ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-y-auto pr-4'
          )}
        >
          {tabs.map(tab => (
            <TabsContent
              key={tab.id}
              value={tab.id}
              className={cn('mt-0', tab.fillAvailableHeight && 'flex min-h-0 flex-1 flex-col')}
            >
              {activeTab === tab.id && children}
            </TabsContent>
          ))}
        </div>
      </div>
    </Tabs>
  );
}

/**
 * Default mobile tabs component with horizontal scrollable layout.
 * Can be used independently or as default renderer for ResponsiveTabs.
 *
 * @example
 * ```tsx
 * // Standalone usage
 * <MobileTabs
 *   activeTab={currentTab}
 *   onTabChange={setCurrentTab}
 *   tabs={[
 *     { id: 'tab1', label: 'Tab 1', icon: Icon1 },
 *     { id: 'tab2', label: 'Tab 2', icon: Icon2 }
 *   ]}
 * />
 *
 * // As custom renderer in ResponsiveTabs
 * <ResponsiveTabs
 *   renderMobileTabs={(props) => <MobileTabs {...props} />}
 *   // ... other props
 * />
 * ```
 */
export function MobileTabs({
  activeTab,
  onTabChange,
  tabs,
}: {
  activeTab: string;
  onTabChange: (tab: string) => void;
  tabs: TabConfig[];
}) {
  return (
    <div className="w-auto flex-shrink-0 rounded-lg bg-muted p-1">
      <div className="flex gap-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && onTabChange(tab.id)}
            disabled={tab.disabled}
            className={cn(
              'flex items-center justify-center gap-2 px-4 py-1.5 rounded-lg transition-all text-sm font-medium whitespace-nowrap relative',
              {
                'cursor-not-allowed opacity-50 text-muted-foreground': tab.disabled,
                'bg-background shadow-sm text-foreground': !tab.disabled && activeTab === tab.id,
                'text-foreground hover:bg-accent': !tab.disabled && activeTab !== tab.id,
              }
            )}
          >
            {tab.icon && <tab.icon className="h-4 w-4" />}
            {tab.label}
            {tab.hasWarning && (
              <TriangleAlertIcon size={16} className="flex-shrink-0 text-amber-600" />
            )}
            {tab.hasError && (
              <TriangleAlertIcon size={16} className="flex-shrink-0 text-destructive" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
